import { Component, OnInit, HostListener, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { AccentService } from '@services/accent-service.service';

@Component({
	selector: 'theme-switcher',
	templateUrl: './theme-switcher.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./theme-switcher.component.scss'],
	host: {
		'[style.top]': 'top()'
	}
})
export class ThemeSwitcherComponent implements OnInit {
	private accent = inject(AccentService);
	private platformId = inject<Object>(PLATFORM_ID);
	private router = inject(Router);
	private readonly isBrowser = isPlatformBrowser(this.platformId);
	isHomePage: boolean = false;

	/**
	 * The `top` style we apply to `:host`.
	 */
	top = signal<string>('');

	constructor() {}

	ngOnInit(): void {
		// Detect if current route is home:
		const url = this.router.url;
		this.isHomePage = (url === '/' || url.startsWith('/home'));

		/**
		 * If in the browser, do an initial scroll-based evaluation.
		 * If on the server (SSR), pick a default.
		 */
		if (this.isBrowser) {
			this.updateTopValue();
		} else {
			// SSR fallback: set a static top so server-side render has no error
			this.top.set(this.isHomePage ? '2rem' : 'calc(2rem + 88px)');
		}
	}

	/**
	 * Use Angular's HostListener for window scroll. 
	 * No raw add/removeEventListener calls, and it only runs in the browser.
	 */
	@HostListener('window:scroll', [])
	onWindowScroll(): void {
		if (!this.isBrowser) return;
		this.updateTopValue();
	}

	/**
	 * Applies the scroll logic:
	 *  - Home Page: always "2rem"
	 *  - Other Pages: 
	 *      if scrollY > 88 => "2rem"
	 *      else => "calc(2rem + 88px)"
	 */
	private updateTopValue(): void {
		if (this.isHomePage) {
			this.top.set('2rem');
			return;
		}

		const scrollY = window.scrollY; // or: document.documentElement.scrollTop
		const threshold = 80; // size of your header

		if (scrollY > threshold) {
			this.top.set('2rem');
		} else {
			this.top.set('calc(2rem + 88px)');
		}
	}

	// -------------------------------------------------
	// THEME LOGIC
	// -------------------------------------------------
	get themeMode(): "light" | "dark" {
		return this.accent.themeMode();
	}

	get themeIcon(): string {
		return this.themeMode === 'light' ? 'dark_mode' : 'light_mode';
	}

	toggleThemeMode() {
		this.setThemeMode(this.themeMode === "light" ? "dark" : "light");
	}

	setThemeMode(mode: "light" | "dark") {
		this.accent.setThemeMode(mode);
	}
}
